import rfc3986EncodeURIComponent from '../../util/encoding/rfc3986EncodeURIComponent.js';
import FacetsModel from './facets_model';
/**
 * Controller logic for facets
 * @see js/public/faceted-media.js
 */

export default class FacetsController {
    constructor() {
        this.SHOW_ONLY_AVAILABLE = 'showOnlyAvailable';
        this.SHOW_ONLY_PRERELEASE = 'showOnlyPrerelease';
        this.ADDED_DATE = 'addedDate';
        this.REMOVE_FILTER = 'removeFilter';
        this.HAS_AUDIO_SYNCHRONIZED_TEXT = 'hasAudioSynchronizedText';
        this.SUBJECT = 'subject';
        this.FORMAT = 'format';
        this.MATURITY_LEVEL = 'maturityLevel';
        this.LANGUAGE = 'language';
        this.PAGE = 'page';
        this.SORT_BY = 'sortBy';
        this.EBOOK_OVERDRIVE_READALONG = 'ebook-overdrive-readalong';
        this.TRUE = 'true';
        this.INTEREST_LEVELS = 'interestLevels';
        this.GRADE_LEVELS = 'gradeLevels';
        this.ATOS_LEVELS = 'atosLevels';
        this.LEXILE_SCORES = 'lexileScores';
        const SINGLE_FACETS = [
            // single key-value pair facets
            this.SHOW_ONLY_AVAILABLE,
            this.SHOW_ONLY_PRERELEASE,
            this.ADDED_DATE,
            this.REMOVE_FILTER,
            this.HAS_AUDIO_SYNCHRONIZED_TEXT,
        ];
        const MULTI_FACETS = [
            // multiple values for each name facets
            this.SUBJECT,
            this.FORMAT,
            this.MATURITY_LEVEL,
            this.LANGUAGE,
        ];
        const RANGE_FACETS = [
            this.INTEREST_LEVELS,
            this.GRADE_LEVELS,
            this.ATOS_LEVELS,
            this.LEXILE_SCORES,
        ];
        this._facetsModel = new FacetsModel(
            SINGLE_FACETS,
            MULTI_FACETS,
            RANGE_FACETS
        );
    }

    /**
     * Returns a count of the facets from the model
     * @returns {*}
     */
    countFacets() {
        return this._facetsModel.countFacets();
    }

    /**
     * Select a facet
     * @param {string} elem Selector string for facet element
     */
    select(elem) {
        // Prevent already active facets from being followed
        if (
            !$(elem).hasClass('js-active') &&
            !$(elem).hasClass('js-disabled')
        ) {
            this._facetsModel.beginTransaction();
            this._facetsModel.deleteOtherParam(this.PAGE);
            if ($(elem).data('facet') === 'narration') {
                this._facetsModel.setFacet(
                    this.HAS_AUDIO_SYNCHRONIZED_TEXT,
                    this.TRUE
                );
            } else {
                const facet = $(elem).attr('data-facet');
                const id = $(elem).attr('data-id');
                if (facet === 'available-now') {
                    this._facetsModel.setFacet(
                        this.SHOW_ONLY_AVAILABLE,
                        this.TRUE
                    );
                    this._facetsModel.unsetFacet(
                        this.SHOW_ONLY_PRERELEASE,
                        this.TRUE
                    );
                } else if (facet === 'all-titles') {
                    this._facetsModel.unsetFacet(
                        this.SHOW_ONLY_AVAILABLE,
                        this.TRUE
                    );
                    this._facetsModel.unsetFacet(
                        this.SHOW_ONLY_PRERELEASE,
                        this.TRUE
                    );
                } else if (facet === 'prerelease') {
                    this._facetsModel.setFacet(
                        this.SHOW_ONLY_PRERELEASE,
                        this.TRUE
                    );
                    this._facetsModel.unsetFacet(
                        this.SHOW_ONLY_AVAILABLE,
                        this.TRUE
                    );
                } else if (facet === this.SUBJECT) {
                    this._facetsModel.setFacet(
                        this.SUBJECT,
                        rfc3986EncodeURIComponent(id)
                    );
                } else if (facet === 'date') {
                    this._facetsModel.setFacet(this.ADDED_DATE, id);
                } else if (facet === 'maturity') {
                    this._facetsModel.setFacet(this.MATURITY_LEVEL, id);
                } else if (
                    [
                        this.FORMAT,
                        this.LANGUAGE,
                        this.INTEREST_LEVELS,
                        this.GRADE_LEVELS,
                        this.ATOS_LEVELS,
                        this.LEXILE_SCORES,
                    ].includes(facet)
                ) {
                    this._facetsModel.setFacet(facet, id);
                }
            }
            if (!this._facetsModel.hasOtherParam(this.SORT_BY)) {
                this._facetsModel.setOtherParam(
                    this.SORT_BY,
                    $('.js-searchSortByOptions').val()
                );
            }
            this._facetsModel.endTransaction();
        }
    }

    /**
     * Remove all active facets
     */
    removeAll() {
        this._facetsModel.beginTransaction();
        this._facetsModel.deleteOtherParam(this.PAGE);
        this._facetsModel.unsetAllFacets();
        this._facetsModel.endTransaction();
    }

    /**
     * Removes `removeFilter` param for rooms
     */
    restoreRoomFilter(filter) {
        this._facetsModel.beginTransaction();
        this._facetsModel.unsetFacet(this.REMOVE_FILTER, filter);
        this._facetsModel.deleteOtherParam(this.PAGE);
        this._facetsModel.endTransaction();
    }

    /**
     * Remove an active facet
     * @param {string} elem Selector string for facet element
     */
    remove(elem) {
        this._facetsModel.beginTransaction();
        const filterId = $(elem).attr('data-filter-id').split(',');
        const filterFacet = $(elem).attr('data-filter-facet');
        // Set page back to 1 when a facet is removed
        this._facetsModel.deleteOtherParam(this.PAGE);
        for (let id of filterId) {
            this._facetsModel.unsetFacet(
                filterFacet,
                rfc3986EncodeURIComponent(id)
            );
            if (filterFacet === this.HAS_AUDIO_SYNCHRONIZED_TEXT) {
                this._facetsModel.unsetFacet(
                    this.FORMAT,
                    this.EBOOK_OVERDRIVE_READALONG
                );
            }
        }
        this._facetsModel.endTransaction();
    }

    /**
     * Remove a room filter
     * Requires adding another query parameter to indicate which filter to ignore
     */
    removeRoomFilter(elem) {
        this._facetsModel.beginTransaction();
        const filterFacet = elem.data('filter-facet');
        const filterId = elem.data('filter-id');
        this._facetsModel.deleteOtherParam(this.PAGE);
        this._facetsModel.unsetFacet(filterFacet, filterId);
        this._facetsModel.setFacet(
            this.REMOVE_FILTER,
            `${filterFacet}-${filterId}`
        );
        this._facetsModel.endTransaction();
    }
}
